// src/pages/BgpStatusPage.tsx (o la ruta que uses)
import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { GenericTable } from "@ui-components/Table/GenericTable";
import { BgpStatus, statusColumns } from "./BgpStatusData";
import { getNeighborsStatus } from "./request/request";
import { GenericFilter } from "@ui-components/Filter/GenericFilter";
import GenericButton from "@ui-components/Button/GenericButton";
import { useAppSelector } from "@redux/hooks";

interface RawNeighborItem {
	neighbor: string;
	as: number;
	local_as: number;
	peer_as: number;
	up: string;
	state: string;
	ctxr: string;
	ctxr_description: string;
	in: number;
	out: number;
	description?: string | null;
}

export function BgpStatusPage() {
	const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
	const { tenantId } = useAppSelector(state => state.selection);


	const [isFetching, setIsFetching] = useState(false);
	const [neighborData, setNeighborData] = useState<BgpStatus[]>([]);

	// Filtro local
	const [searchTerm, setSearchTerm] = useState("");
	const [searchColumn, setSearchColumn] = useState("All");

	// 1) Encapsulamos la llamada GET en una función para poder reusarla (useEffect + Refresh)
	const fetchNeighbors = async () => {
		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();
			const resp = await getNeighborsStatus(token, tenantId);

			// 'resp.data' es un array con elementos del tipo 'RawNeighborItem'
			const shaped: BgpStatus[] = (resp.data as RawNeighborItem[]).map(
				(item) => ({
					ctxr: item.ctxr ?? "",
					ctxr_description: item.ctxr_description ?? "",
					neighbor: item.neighbor ?? "",
					description: item.description ?? "",
					state: item.state ?? "",
					up: item.up ?? "",
					local_as: item.local_as ?? "",
					peer_as: item.peer_as ?? "",
					in: item.in || 0,
					out: item.out || 0,
				})
			);
			console.log(resp.data);
			setNeighborData(shaped);
		} catch (err) {
			console.error("Error fetching neighbors =>", err);
		} finally {
			setIsFetching(false);
		}
	};

	// 2) Llamamos fetchNeighbors al montar (si está logueado)
	useEffect(() => {
		if (isLoading) return;

		if (!isAuthenticated) {
			console.warn("User not authenticated; skipping fetch.");
			return;
		}

		if (tenantId === 0) {
			console.log("No tenantId seleccionado todavía; no hacemos fetch...");
			return;
		}

		fetchNeighbors();
	}, [
		isLoading,
		isAuthenticated,
		getAccessTokenSilently,
		tenantId,
	]);

	// 3) Botón Refresh
	const handleRefresh = async () => {
		console.log("Refresh button clicked!");
		await fetchNeighbors();
	};

	// Filtro local
	const filteredData = neighborData.filter((row) => {
		const term = searchTerm.toLowerCase();
		if (!term) return true;

		const includes = (val: string) => val.toLowerCase().includes(term);

		switch (searchColumn) {
			case "CTX-R":
				return includes(row.ctxr) || includes(row.ctxr_description);
			case "Neighbor":
				return includes(row.neighbor) || includes(row.description ?? "");
			case "Status/Time":
				return includes(row.state) || includes(row.up);
			case "AS":
				return (
					includes(row.local_as.toString()) ||
					includes(row.peer_as.toString())
				);
			case "Prefix":
				return includes(row.in.toString()) || includes(row.out.toString());
			case "All":
			default:
				return (
					includes(row.ctxr) ||
					includes(row.ctxr_description) ||
					includes(row.neighbor) ||
					includes(row.description ?? "") ||
					includes(row.state) ||
					includes(row.up) ||
					includes(row.local_as.toString()) ||
					includes(row.peer_as.toString()) ||
					includes(row.in.toString()) ||
					includes(row.out.toString())
				);
		}
	});

	return (
		<div className="relative">
			{/* Barra superior con el filtro y el botón Refresh */}
			<div className="mb-4 bg-[var(--semantic-bg-primary)]">
				<div className="flex items-end justify-between">
					<div className="flex items-end space-x-4" />
					<div className="flex items-center space-x-4">
						<GenericFilter
							searchColumn={searchColumn}
							onSearchColumnChange={setSearchColumn}
							searchTerm={searchTerm}
							onSearchTermChange={setSearchTerm}
							columnOptions={[
								"All",
								"CTX-R",
								"Neighbor",
								"Status/Time",
								"AS",
								"Prefix",
							]}
						/>
						<GenericButton
							label=""
							iconLeftName="refresh"
							variant="secondary"
							shape="ghost"
							size="md"
							onClick={handleRefresh}
						/>
					</div>
				</div>
			</div>

			{/* Tabla */}
			<GenericTable
				columns={statusColumns}
				data={filteredData}
				initialRowsPerPage={5}
				rowsPerPageOptions={[5, 10]}
				width="100%"
				tableClassName="bg-semantic-bg-primary"
			/>

			{/* Overlay spinner */}
			{isFetching && (
				<div className="fixed inset-0 flex flex-col items-center justify-center bg-black/50 z-[9999]">
					<h2 className="text-xl font-semibold mb-4 text-white">
						Processing request...
					</h2>
					<div
						className="
              animate-spin
              rounded-full
              h-12 w-12
              border-4 border-gray-300
              border-t-blue-500
            "
					/>
				</div>
			)}
		</div>
	);
}

export default BgpStatusPage;
